import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { digitalApi as api } from './api';
import { useAuth } from '../../context/AuthContext';
import DigitalPlanningEngine from './DigitalPlanningEngine';
import { C } from './styles';

const STATUS_COLOR = { draft: C.text3, review: C.amber, approved: C.green, archived: '#AAA' };
const STATUS_ICON  = { draft:'✏️', review:'🔍', approved:'✅', archived:'📦' };

export default function DigitalPlanEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit, canApprove, isAdmin } = useAuth();

  const [plan, setPlan]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error

  const load = useCallback(() => {
    api.getPlan(id).then(p => {
      setPlan(p);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleSave = useCallback(async (engineData) => {
    setSaveState('saving');
    try {
      await api.savePlan(id, { data: engineData });
      setPlan(p => ({ ...p, data: engineData }));
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 2000);
    } catch(e) {
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
    }
  }, [id]);

  async function handleStatusAction(action) {
    if (!window.confirm(`${action} this plan?`)) return;
    try {
      if (action === 'Submit for Review') await api.submitPlan(id);
      if (action === 'Approve')           await api.approvePlan(id);
      if (action === 'Reject to Draft')   await api.rejectPlan(id);
      if (action === 'Archive')           await api.archivePlan(id);
      load();
    } catch(e) { alert(e.message); }
  }

  const isReadOnly = !canEdit ||
    (plan?.status === 'approved' && !isAdmin) ||
    plan?.status === 'archived';

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#F5F5F5', color:'#9A8A7A', fontFamily:"'Inter',sans-serif" }}>
      Loading plan…
    </div>
  );

  if (!plan) return (
    <div style={{ padding:40, color:'#A02020', fontFamily:"'Inter',sans-serif" }}>Plan not found.</div>
  );

  // Build initial data for engine from saved plan data
  const initialData = (plan.data && plan.data.flights && plan.data.flights.length > 0)
    ? { ...plan.data, planName: plan.name }
    : (plan.data && plan.data.brands && plan.data.brands.length > 0)
    ? { ...plan.data, planName: plan.name }
    : {
        planName: plan.name,
        year: plan.year || 2026,
        universe: 531192,
        brands: [{ id: 1, name: 'Brand 1', flights: [] }],
        platforms: [],
      };

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100vh', overflow:'hidden' }}>
      {/* ── Thin status bar above engine ── */}
      <div style={{
        display:'flex', alignItems:'center', gap:12, padding:'6px 16px',
        background:'#FFFFFF', borderBottom:`2px solid #E8D5BC`, flexShrink:0,
        fontFamily:"'Inter',sans-serif",
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background:'transparent', border:`1px solid #E0E0E0`, borderRadius:6, padding:'4px 10px', fontSize:12, color:'#4A4A4A', cursor:'pointer', fontFamily:"'Inter',sans-serif" }}>
          ← Back
        </button>

        <div style={{ width:1, height:20, background:'#E0E0E0' }} />

        <span style={{ fontSize:13, fontWeight:700, color:'#0A0A0A' }}>{plan.name}</span>
        <span style={{
          padding:'2px 9px', borderRadius:20, fontSize:11, fontWeight:700,
          color: STATUS_COLOR[plan.status], background: (STATUS_COLOR[plan.status]) + '18',
          border: `1px solid ${STATUS_COLOR[plan.status]}44`,
        }}>
          {STATUS_ICON[plan.status]} {plan.status}
        </span>
        <span style={{ fontSize:11, color:'#9A8A7A' }}>v{plan.version || 1}</span>

        {saveState === 'saving' && <span style={{ fontSize:11, color:'#C19A6B' }}>⏳ Saving…</span>}
        {saveState === 'saved'  && <span style={{ fontSize:11, color:'#2A7A5A' }}>✓ Saved</span>}
        {saveState === 'error'  && <span style={{ fontSize:11, color:'#A02020' }}>✗ Save failed</span>}

        <div style={{ flex:1 }}/>

        {/* Status actions */}
        {plan.status === 'draft' && canEdit && (
          <button onClick={() => handleStatusAction('Submit for Review')}
            style={{ background:'transparent', border:`1px solid #B8860B44`, borderRadius:6, padding:'4px 10px', fontSize:11, color:'#B8860B', cursor:'pointer', fontFamily:"'Inter',sans-serif" }}>
            Submit for Review
          </button>
        )}
        {plan.status === 'review' && canApprove && (<>
          <button onClick={() => handleStatusAction('Approve')}
            style={{ background:'transparent', border:`1px solid #2A7A5A44`, borderRadius:6, padding:'4px 10px', fontSize:11, color:'#2A7A5A', cursor:'pointer', fontFamily:"'Inter',sans-serif" }}>
            ✓ Approve
          </button>
          <button onClick={() => handleStatusAction('Reject to Draft')}
            style={{ background:'transparent', border:`1px solid #A0202044`, borderRadius:6, padding:'4px 10px', fontSize:11, color:'#A02020', cursor:'pointer', fontFamily:"'Inter',sans-serif" }}>
            ✗ Reject
          </button>
        </>)}
        {isAdmin && plan.status !== 'archived' && (
          <button onClick={() => handleStatusAction('Archive')}
            style={{ background:'transparent', border:`1px solid #E0E0E0`, borderRadius:6, padding:'4px 10px', fontSize:11, color:'#9A8A7A', cursor:'pointer', fontFamily:"'Inter',sans-serif" }}>
            Archive
          </button>
        )}
      </div>

      {/* ── Digital Planning Engine takes remaining height ── */}
      <div style={{ flex:1, overflow:'hidden' }}>
        <DigitalPlanningEngine
          initialData={initialData}
          planName={plan.name}
          onSave={handleSave}
          readOnly={isReadOnly}
          clientId={plan.client_id}
          planStatus={plan.status}
        />
      </div>
    </div>
  );
}
